import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { CircleAlert, XIcon } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { SurgeonAttachmentReminderModal } from './SurgeonAttachmentReminderModal';
import { UserProfileSidebar } from './UserProfileSidebar';
import { AuthCard } from './authCard';
import { AdditionalExpertsSection } from './form/AdditionalExpertsSection';
import { CancerTypeSelect } from './form/CancerTypeSelect';
import { FileUploadField } from './form/FileUploadField';
import { MessageField } from './form/MessageField';
import { NoPHIField } from './form/NoPHIField';
import { ProfileChips } from './profile/ProfileChips';

import { headerItem } from '@/components/HeaderActions';
import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import TransitionScreen from '@/components/transitionScreen';
import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { SPECIALTY_BADGE_CLASS, SPECIALTY_BADGE_TEXT_CLASS } from '@/constants/badges';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { needsVerificationToSubmitInquiry, useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useInquiryForm } from '@/hooks/useInquiryForm';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useProductSupportPostSubmit } from '@/hooks/useProductSupportPostSubmit';
import {
  getAlgoliaExpertByIdQueryOptions,
  getAlgoliaExpertsInfiniteQueryOptions,
} from '@/services/getAlgoliaExperts';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { getUserPublicV2QueryOptions } from '@/services/getUserPublicV2';
import { productSupportPrefillMessageAtom, userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { resolveSelectedExperts } from '@/util/inquiryExperts';
import { isTestUser } from '@/util/isTestUser';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import { shownTrialCount } from '@/util/profileTrialCounts';
import { isAskAnonymously } from '@/util/userFilters';
import { isSurgeon } from '@/utils/expertHelpers';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-xl' });

const SCREEN_TITLE = 'New Inquiry';

/**
 * Medical Inquiry Form with Multi-Expert Support
 *
 * Handles creation and submission of medical inquiries to cancer experts. Supports both
 * single-expert (default) and multi-expert (opt-in) workflows using progressive disclosure
 * to maintain simplicity while enabling advanced functionality when needed.
 *
 * **User Flows:**
 * **Single-Expert (Default):**
 * Cancer Type → Message → Submit → Single Expert Receives Inquiry
 *
 * **Multi-Expert (Opt-in):**
 * Cancer Type → Enable Additional Experts → Select Experts → Submit → Multiple Experts Receive Inquiry
 *
 * **Form Logic:**
 * - **Progressive Disclosure:** Multi-expert option unlocked by cancer type selection
 * - **Smart Filtering:** Additional experts filtered by cancer specialization, excluding primary expert
 * - **Conditional Validation:** Additional expert limits only apply when feature is enabled
 * - **Auto-Save Integration:** Preserves user progress while maintaining active selections
 * - **Expert Selection Clearing:** Additional experts (not primary) are cleared when cancer type changes
 * - **Draft Expert Selection:** Additional experts are NOT saved in drafts - only primary expert (expertId) is saved for routing
 *
 * **Surgeon Behavior:**
 * - **Additional Experts:** All experts within the selected cancer type are available as additional experts
 * - **Surgeon Detection:** Based on specialty field containing "surgical oncology" (case-insensitive)
 * - **Attachment Guidance:** Shows surgical attachment info box when ANY selected expert (primary or additional) is a surgeon
 * - **Video Attachment Reminder:** Shows Important Notice modal when submitting to any surgeon without attachments
 * - **Attachment Guidance:** Modal provides surgical-specific file upload recommendations and PHI guidelines
 *
 * **API Integration:**
 * Directory Inquiry (`/directory/inquiry/[id]`) submission format:
 * - Single: `expertId: <id>, expertIds: [<id>]` (expertId from URL route parameter, expertIds contains primary expert as first element - API requirement)
 * - Multi: `expertId: <id>, expertIds: [<id>, ...additionalExpertIds]` (expertId from URL, expertIds contains primary expert as first element plus additional experts)
 * - Note: API requires expertId to match the first element in expertIds array when provided
 * - Backend creates linked conversation threads for each expert
 *
 * **Endpoint Strategy:**
 * - Uses public endpoint for pending OAuth users, private endpoint for authenticated users with DB record
 */
export default function DraftInquiry() {
  const router = useRouter();

  const { replace } = router;

  const setPrefillMessage = useSetAtom(productSupportPrefillMessageAtom);

  const institutionalTrialsEnabled = useFeatureFlag(FEATURE_FLAGS.INSTITUTIONAL_TRIALS);

  const isModProductSupportSuccessEnabled = useFeatureFlag(
    FEATURE_FLAGS.MOD_PRODUCT_SUPPORT_SUCCESS_SCREEN
  );

  const isInquiryMultiExpertNotesEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_MULTI_EXPERT_NOTES);

  const { isPrimumUser } = useCognitoGroups();

  const insets = useSafeAreaInsets();

  const { navigateBack } = useBackNavigation(ROUTES.DIRECTORY);

  const { id, focusId, draftId } = useLocalSearchParams<{
    id: string;
    focusId: string;
    draftId: string;
  }>();

  const userId = useAtomValue(userIdAtom);

  const isLoggedIn = !!userId;

  const { requestAccess } = useAbilityGate();

  const currentUserQuery = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const expertByIdQuery = useQuery(getAlgoliaExpertByIdQueryOptions(id));

  const expert = expertByIdQuery.data ?? undefined;

  const isLoading = expertByIdQuery.isLoading;

  const publicExpertQuery = useQuery({
    ...getUserPublicV2QueryOptions(id, { retryFailedOnMount: false }),
    enabled: !!id,
  });

  const expertData = publicExpertQuery.data;

  const isExpertLoading = publicExpertQuery.isLoading;

  const cancerFocusQuery = useQuery(getCancerTypesV2QueryOptions());

  const expertCancerFocuses = expertData?.cancerFocusIds || [];

  const selectedCancerFocusId = useMemo(() => {
    const singleCancerFocusId =
      expertCancerFocuses.length === 1 ? (expertCancerFocuses[0]?.id?.toString() ?? '') : '';

    return focusId || singleCancerFocusId || '';
  }, [expertCancerFocuses, focusId]);

  const { captureSubmissionSnapshot, resolvePostSubmitSuccess } = useProductSupportPostSubmit({
    isModProductSupportSuccessEnabled,
    source: 'draft_inquiry',
  });

  const {
    control,
    errors,
    formValues,
    isValid,
    setValue,
    submitInquiry,
    deleteDraft,
    isSubmitting,
    isDeleteDisabled,
    isDeleting,
    submissionStatus,
  } = useInquiryForm({
    expertId: id,
    currentDraftId: draftId,
    selectedCancerFocusId,
    onSuccess: (expertIds, cancerFocusId, inquiryId) => {
      const processInquirySuccess = async () => {
        const firstExpertId = expertIds[0] || id;

        const { successState } = await resolvePostSubmitSuccess({ inquiryId });

        const params = new URLSearchParams({
          id: String(firstExpertId),
          focusId: String(cancerFocusId),
        });

        if (successState.showSuccess) {
          params.set('productSupport', '1');
        }

        if (successState.showSuccess && successState.productId) {
          params.set('productId', successState.productId);
        }

        if (successState.showSuccess && successState.prefillMessage) {
          setPrefillMessage(successState.prefillMessage);
        }

        replace(`/directory/inquiry/success?${params.toString()}`);
      };

      processInquirySuccess().catch(() => undefined);
    },
  });

  const cancerFocusId = formValues.cancerFocusId || '';

  const selectedCancerTypeLabel = useMemo(() => {
    if (!cancerFocusId || !cancerFocusQuery.data) {
      return '';
    }

    const cancerType = cancerFocusQuery.data.cancerTypeLookup.get(Number(cancerFocusId));

    return cancerType?.label || '';
  }, [cancerFocusId, cancerFocusQuery.data]);

  const hasAttachment = useMemo(() => {
    return formValues.files && formValues.files.length > 0;
  }, [formValues.files]);

  const {
    data: filteredExpertsData,
    isLoading: isLoadingFilteredExperts,
    isFetching: isFetchingFilteredExperts,
  } = useInfiniteQuery({
    ...getAlgoliaExpertsInfiniteQueryOptions(
      {
        searchQuery: '',
        focusId: cancerFocusId,
      },
      { hitsPerPage: 100, maxPages: 1 }
    ),
    enabled: !!cancerFocusId,
    placeholderData: (previousData) => previousData,
  });

  const allFilteredExperts = filteredExpertsData?.pages?.flatMap((page) => page.hits) || [];

  const filteredExperts = useMemo(() => {
    return allFilteredExperts
      .filter((expert) => expert.id !== id)
      .filter((expert) => expert.id !== userId)
      .filter((expert) => !isTestUser(expert))
      .sort((a, b) => {
        const nameA = a.nameWithoutDoctor || a.name;

        const nameB = b.nameWithoutDoctor || b.name;

        return nameA.localeCompare(nameB);
      });
  }, [allFilteredExperts, id, userId]);

  const hasAnySurgeonExpert = useMemo(() => {
    const allSelectedExperts = [
      expert,
      ...(formValues.expertIds?.map((expertId) =>
        allFilteredExperts.find((candidate) => candidate.id === String(expertId))
      ) || []),
    ].filter(Boolean);

    return allSelectedExperts.some((selected) => isSurgeon(selected?.speciality));
  }, [expert, formValues.expertIds, allFilteredExperts]);

  const selectedExperts = useMemo(() => {
    return resolveSelectedExperts({
      expert,
      expertIds: formValues.expertIds,
      allExperts: allFilteredExperts,
    });
  }, [allFilteredExperts, expert, formValues.expertIds]);

  const prevCancerFocusIdRef = useRef<string>('');

  /**
   * Clears additional experts (not primary) when cancer type changes.
   * Primary expert (expertId) is preserved as it's tied to the route.
   */
  useEffect(() => {
    const prevCancerFocusId = prevCancerFocusIdRef.current;

    if (prevCancerFocusId && prevCancerFocusId !== cancerFocusId) {
      setValue('enableExpertSelection', false, { shouldDirty: false });

      setValue('expertIds', [], { shouldDirty: false });
    }

    if (cancerFocusId) {
      prevCancerFocusIdRef.current = cancerFocusId;
    }
  }, [cancerFocusId, setValue]);

  const isAnonymous = isAskAnonymously(expert?.name);

  useEffect(() => {
    if (isAnonymous) {
      setValue('anonymous', true, { shouldDirty: false });
    }
  }, [isAnonymous, setValue]);

  const [showModal, setShowModal] = useState(false);

  const [showReminderModal, setShowReminderModal] = useState(false);

  const [triggerFilePicker, setTriggerFilePicker] = useState(false);

  const isExpertAway = expertData?.status === '2';

  const isSubmittingToSelf = isLoggedIn && userId === id;

  const ability = useAbility();

  const needsNpiVerification = isLoggedIn && needsVerificationToSubmitInquiry(ability);

  const isSubmitDenied = isLoggedIn && !ability.can('submit', 'Inquiry');

  const formDisabled = !userId || isSubmittingToSelf;

  const isAwayBlocked = isExpertAway && !isPrimumUser;

  const isMaintenanceMode = useMaintenanceMode();

  const isSubmitVisuallyDisabled =
    !isValid || isMaintenanceMode || isAwayBlocked || isSubmittingToSelf || isSubmitDenied;

  const isSubmissionInProgress = isSubmitting || !!submissionStatus;

  const capturePostSubmitSnapshot = () => {
    captureSubmissionSnapshot({
      message: formValues.message,
      cancerFocusId: formValues.cancerFocusId,
    });
  };

  const handleSubmitInquiry = async () => {
    capturePostSubmitSnapshot();

    if (!isValid) {
      await submitInquiry();

      return;
    }

    if (hasAnySurgeonExpert && !hasAttachment && !showReminderModal) {
      setShowReminderModal(true);

      return;
    }

    await submitInquiry();
  };

  const handleSubmitInquiryFromModal = () => {
    setShowReminderModal(false);

    capturePostSubmitSnapshot();

    submitInquiry();
  };

  const handleAttachFilesFromModal = () => {
    setTriggerFilePicker(true);
  };

  const handleTriggerComplete = () => {
    setTriggerFilePicker(false);
  };

  if (isSubmissionInProgress) {
    return <TransitionScreen message={submissionStatus || 'Submitting your inquiry ...'} />;
  }

  const deleteDraftItem = headerItem.deleteDraft(() => {
    segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_STARTED, {
      draftId,
    });

    setShowModal(true);
  }, isDeleteDisabled);

  return (
    <VStack className="flex-1">
      <VStack
        className={twJoin('flex-1', SCREEN_COLUMN.frame)}
        style={{ paddingBottom: insets.bottom }}>
        <Header
          title={SCREEN_TITLE}
          backRoute={ROUTES.DIRECTORY}
          rightItems={[deleteDraftItem]}
          contentScrollsUnderHeader
          isBackButton
        />

        <HStack className="md:mt-4 md:gap-x-10">
          <KeyboardAwareScrollView
            mode="layout"
            contentInsetAdjustmentBehavior="automatic"
            bottomOffset={18}
            className="flex-1"
            contentContainerClassName={twJoin('py-5 web:md:pt-0', SCREEN_COLUMN.content)}
            showsVerticalScrollIndicator={false}
            keyboardDismissMode="interactive"
            keyboardShouldPersistTaps="handled">
            {isExpertAway && (
              <HStack className="mb-4 w-full items-start bg-info-0 p-4">
                <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-info-800" />
                <VStack className="ml-3 w-0 flex-1">
                  <Heading size="lg" className="text-info-800">
                    Your expert is currently unavailable
                  </Heading>
                </VStack>
              </HStack>
            )}

            {isSubmittingToSelf && (
              <HStack className="mb-4 w-full items-start bg-warning-0 p-4">
                <Icon as={CircleAlert} className="mt-0.5 flex-shrink-0 text-warning-800" />
                <VStack className="ml-3 w-0 flex-1">
                  <Heading size="lg" className="text-warning-800">
                    Cannot submit inquiry to yourself
                  </Heading>
                  <Text className="text-warning-700">
                    You cannot send an inquiry to your own profile. Please select a different
                    expert.
                  </Text>
                </VStack>
              </HStack>
            )}
            {isWeb && !userId && (
              <AuthCard
                className="web:mb-6"
                text="Sign in or create an account to submit an inquiry"
              />
            )}

            {isAnonymous ? (
              <VStack className="w-full md:hidden">
                <Card variant="filled" className="w-full md:hidden">
                  <VStack>
                    <HStack className="mb-2 items-center">
                      <Avatar className="mr-3">
                        <AvatarFallbackText>Ask Anonymously</AvatarFallbackText>
                        <AvatarImage
                          source={expert?.avatarSrc ? { uri: expert.avatarSrc } : undefined}
                        />
                      </Avatar>
                      <Heading size="h2-xl" className="mb-2 flex-shrink text-typography-900">
                        Ask Anonymously
                      </Heading>
                    </HStack>
                    <Text className="text-wrap text-typography-700">
                      Submit your inquiries anonymously.
                    </Text>
                  </VStack>
                </Card>
              </VStack>
            ) : (
              <Card variant="filled" className="w-full md:hidden" data-component="expert-profile">
                <VStack>
                  <HStack className="items-center">
                    <Avatar className="mr-3">
                      <Skeleton className="h-12 w-12 rounded-full" isLoaded={!isLoading}>
                        <AvatarFallbackText>{expert?.name || ''}</AvatarFallbackText>
                        <AvatarImage
                          source={expert?.avatarSrc ? { uri: expert.avatarSrc } : undefined}
                        />
                      </Skeleton>
                    </Avatar>
                    <VStack className="flex-1 items-baseline">
                      <SkeletonText className="h-6 w-1/2" isLoaded={!isLoading}>
                        <Heading size="h2-xl" className="mb-2 flex-shrink text-typography-900">
                          {expert?.name ?? ''}
                        </Heading>
                      </SkeletonText>
                      {expert?.speciality && (
                        <Badge
                          action="info"
                          size="lg"
                          className={twJoin('lg:mb-0', SPECIALTY_BADGE_CLASS)}>
                          <BadgeText className={SPECIALTY_BADGE_TEXT_CLASS}>
                            {expert?.speciality || ''}
                          </BadgeText>
                        </Badge>
                      )}
                    </VStack>
                  </HStack>
                  <Divider className="my-4" />
                  <VStack className="gap-2">
                    {expert?.institution && (
                      <HStack className="items-start gap-2">
                        <Text className="text-sm text-typography-700">
                          {expert?.institution || ''}
                        </Text>
                      </HStack>
                    )}
                    {expert?.title && (
                      <HStack className="items-start gap-2">
                        <SkeletonText className="h-4 w-full" isLoaded={!isLoading}>
                          <Text className="text-wrap text-sm text-typography-700">
                            {expert?.title || ''}
                          </Text>
                        </SkeletonText>
                      </HStack>
                    )}
                    {expertData?.id ? (
                      <ProfileChips
                        expertId={expertData.id}
                        publicationsCount={expertData.publicationsCount ?? 0}
                        clinicalTrialsCount={shownTrialCount(
                          expertData,
                          institutionalTrialsEnabled
                        )}
                        className="mt-1"
                        showChevron={false}
                        onSelect={(target) => {
                          router.push({
                            pathname: '/profile/[id]',
                            params: { id: String(expertData.id), section: target },
                          });
                        }}
                      />
                    ) : null}
                  </VStack>
                </VStack>
              </Card>
            )}

            <VStack className="flex-shrink pb-5 pt-5 md:pt-0">
              {/* Required legend */}
              <HStack className="mb-4">
                <Text className="text-xs text-typography-700">* Required</Text>
              </HStack>

              <CancerTypeSelect
                control={control}
                errors={errors}
                isDisabled={formDisabled}
                expertCancerFocuses={expertCancerFocuses}
              />

              {!isAnonymous && (
                <VStack className="mt-4">
                  <AdditionalExpertsSection
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    isDisabled={formDisabled}
                    cancerFocusId={cancerFocusId}
                    enableExpertSelection={formValues.enableExpertSelection || false}
                    filteredExperts={filteredExperts}
                    isLoadingFilteredExperts={isLoadingFilteredExperts}
                    isFetchingFilteredExperts={isFetchingFilteredExperts}
                    isDraft={!!draftId}
                    cancerTypeLabel={selectedCancerTypeLabel}
                    currentUser={
                      currentUserQuery.data
                        ? {
                            firstName: currentUserQuery.data.firstName ?? undefined,
                            lastName: currentUserQuery.data.lastName ?? undefined,
                            email: currentUserQuery.data.email || undefined,
                          }
                        : undefined
                    }
                  />
                </VStack>
              )}

              <VStack className="mt-4">
                <MessageField
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  isDisabled={formDisabled}
                  previewExperts={isInquiryMultiExpertNotesEnabled ? selectedExperts : []}
                />
              </VStack>

              <VStack className="mt-3">
                <FileUploadField
                  control={control}
                  errors={errors}
                  isDisabled={formDisabled}
                  isExpertSurgeon={hasAnySurgeonExpert}
                  triggerFilePicker={triggerFilePicker}
                  onTriggerComplete={handleTriggerComplete}
                  supportTextClassName={twJoin(!hasAttachment && 'web:mb-9')}
                />

                <NoPHIField
                  control={control}
                  errors={errors}
                  isDisabled={formDisabled}
                  hasFiles={!!(formValues.files && formValues.files.length > 0)}
                />
              </VStack>

              <Divider className="mb-4 web:mb-9" />

              <ConfirmationCheckbox
                control={control}
                errors={errors}
                fieldName="noPII"
                variant="pii"
                isDisabled={formDisabled}
                className="mb-6"
              />
              <Button
                onPress={handleSubmitInquiry}
                isDisabled={
                  isMaintenanceMode ||
                  isSubmissionInProgress ||
                  formDisabled ||
                  !isValid ||
                  isAwayBlocked
                }
                className={twJoin(
                  'web:w-fit web:self-center',
                  isSubmitVisuallyDisabled ? 'opacity-50' : ''
                )}
                testID="submit-inquiry-button">
                <ButtonText>Submit inquiry</ButtonText>
              </Button>
            </VStack>
          </KeyboardAwareScrollView>

          <VStack className="relative hidden flex-1 pb-5 md:flex">
            {isLoading || isExpertLoading ? (
              <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0 flex-1" />
            ) : (
              <UserProfileSidebar expert={expert} expertId={id} />
            )}
          </VStack>
        </HStack>
      </VStack>

      {isWeb && needsNpiVerification ? (
        <View
          className="absolute left-0 top-0 h-full w-full"
          onStartShouldSetResponder={() => {
            requestAccess();

            return false;
          }}
        />
      ) : null}

      <Modal
        isOpen={showModal}
        onClose={() => {
          segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

          setShowModal(false);
        }}
        size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader className="pb-5">
            <Heading size="h2-xl">Delete draft</Heading>
            <ModalCloseButton
              onPress={() => {
                segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

                setShowModal(false);
              }}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody className="pb-6">
            <Text className="text-typography-700">Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-3 pt-0">
            <Button
              disabled={isDeleting}
              action="negative"
              className={twJoin(
                'w-full border-warning-600 bg-warning-600 data-[hover=true]:border-warning-700 data-[hover=true]:bg-warning-700 data-[active=true]:border-warning-800 data-[active=true]:bg-warning-800',
                isDeleting && 'opacity-50'
              )}
              onPress={() => {
                setShowModal(false);

                deleteDraft(() => navigateBack());
              }}>
              <ButtonText className="text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0">
                Delete draft
              </ButtonText>
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => {
                segmentClient.track(ANALYTICS_EVENTS.DELETE_DRAFT_CANCELLED, { draftId });

                setShowModal(false);
              }}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SurgeonAttachmentReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSubmitInquiry={handleSubmitInquiryFromModal}
        onAttachFiles={handleAttachFilesFromModal}
        expertId={id}
        hasAttachment={hasAttachment}
      />
    </VStack>
  );
}
